// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/CompetitorsCard.tsx (card, list, rows, footer)
// Ours on top of TP: max 3 and the disabled look at the cap, preselection, busy (Button loading), the save error,
// "Still searching", a live count, one Skip that is always there (TP's Confirm is disabled with nothing chosen), and
// no search field (the lists hold a handful of rows). TP's rows are a fixed `h-11`; ours grow (`min-h-11`) so a
// second line can never be drawn over the name.
import { Check, Sparkles } from 'lucide-react';
import { useId, useState } from 'react';

import { DockedQuestionCard } from '@/components/rooms';
import { BrandLogo } from '@/components/ui/brand-logo';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import { CONFIRM_PANEL, COPY } from '../copy';
import { PANEL_CLICK_GUARD_MS, useClickGuard } from '../hooks/useClickGuard';

export interface ConfirmItem {
  id: string;
  label: string;
  /** Short facts on the right of the row ("12 ads"). */
  hint?: string;
  /** Facts about the row, one muted line under the name. */
  detail?: string;
  /** Where the row comes from, as a small badge after the name. */
  note?: string;
  /** A logo or avatar URL from the BFF (http/https only); without one (or when it fails) the row shows the name's initial. */
  imageUrl?: string | null;
}

export interface ConfirmPanelProps {
  title: string;
  subtitle?: string;
  items: readonly ConfirmItem[];
  /** Ids ticked from the start (the shortlist is preselected). */
  initialSelected?: readonly string[];
  /** The most that can be chosen; further rows are disabled once reached. */
  max?: number;
  /** More rows may still arrive (the discovery is running). */
  searching?: boolean;
  /** The save is in flight. */
  busy?: boolean;
  /** A short message when the last save failed; the choice stays so it can be retried. */
  error?: string | null;
  onConfirm: (ids: string[]) => void;
  onSkip: () => void;
  /** Clicks are ignored this long after the panel mounts (a double-click on the step before must not land here). */
  clickGuardMs?: number;
}

/**
 * A docked multi-choice over things the research found (ad pages, competitors), TP's CompetitorsCard: one list of
 * label rows with a logo, name, facts and a checkbox. Tick any (up to `max`), then Confirm, or Skip. Mount with a
 * `key` so each step starts from its own preselection.
 */
export function ConfirmPanel({
  title,
  subtitle,
  items,
  initialSelected = [],
  max,
  searching = false,
  busy = false,
  error = null,
  onConfirm,
  onSkip,
  clickGuardMs = PANEL_CLICK_GUARD_MS,
}: ConfirmPanelProps) {
  const groupId = useId();
  const guard = useClickGuard(clickGuardMs);
  // A preselection longer than the cap is cut to it: the panel never starts above its own limit.
  const [selected, setSelected] = useState<string[]>(() => (max !== undefined ? initialSelected.slice(0, max) : [...initialSelected]));
  const atMax = max !== undefined && selected.length >= max;

  const toggle = guard((id: string) => {
    if (busy) return;
    // The cap is checked against the state the update sees, not the render's: two quick toggles cannot both slip past it.
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : max !== undefined && prev.length >= max ? prev : [...prev, id]));
  });
  const confirm = guard(() => {
    if (selected.length > 0) onConfirm(selected);
  });
  const skip = guard(onSkip);

  const status = max !== undefined ? `${selected.length} of ${max} chosen` : `${selected.length} selected`;

  return (
    <DockedQuestionCard
      focusHeading
      title={title}
      subtitle={subtitle}
      titleIcon={<Sparkles aria-hidden="true" className="size-4 text-ds-text-2" />}
      titleId={groupId}
      headerAccessory={
        searching ? (
          <span className="flex shrink-0 items-center gap-1.5 text-label text-ds-text-2">
            <Spinner size="sm" />
            {CONFIRM_PANEL.searching}
          </span>
        ) : null
      }
      footer={
        <>
          <span className="flex-1 text-label text-ds-text-2" aria-live="polite">
            {status}
          </span>
          <Button type="button" variant="ghost" size="xs" disabled={busy} onClick={skip}>
            {COPY.skip}
          </Button>
          <Button type="button" size="xs" loading={busy} disabled={selected.length === 0} onClick={confirm}>
            {busy ? null : <Check aria-hidden="true" />}
            {COPY.confirm}
          </Button>
        </>
      }
    >
      <ul
        aria-labelledby={groupId}
        aria-busy={busy}
        className="max-h-56 overflow-y-auto rounded-md border border-ds-hairline"
      >
        {items.map((item) => {
          const isSelected = selected.includes(item.id);
          const locked = atMax && !isSelected;
          const checkId = `${groupId}-${item.id}`;
          const describedBy = [item.detail ? `${checkId}-detail` : null, item.hint ? `${checkId}-hint` : null]
            .filter(Boolean)
            .join(' ');
          return (
            <li key={item.id} className="border-b border-ds-hairline last:border-b-0">
              <label
                htmlFor={checkId}
                className={cn(
                  'flex min-h-11 items-center gap-2.5 px-3 py-1.5',
                  locked ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-ds-ink-a05',
                )}
              >
                <BrandLogo name={item.label} src={item.imageUrl} className="size-6" />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={cn('min-w-0 truncate text-tab', locked ? 'text-ds-disabled-text' : 'text-foreground')}>
                      {item.label}
                    </span>
                    {item.note ? (
                      <Badge intent="neutral" className="shrink-0">
                        <Sparkles aria-hidden="true" className="size-3.5" />
                        {item.note}
                      </Badge>
                    ) : null}
                  </span>
                  {item.detail ? (
                    <span
                      id={`${checkId}-detail`}
                      className={cn('block truncate text-label', locked ? 'text-ds-disabled-text' : 'text-ds-text-3')}
                    >
                      {item.detail}
                    </span>
                  ) : null}
                </span>
                {item.hint ? (
                  <span
                    id={`${checkId}-hint`}
                    className={cn('shrink-0 text-label', locked ? 'text-ds-disabled-text' : 'text-ds-text-2')}
                  >
                    {item.hint}
                  </span>
                ) : null}
                <Checkbox
                  id={checkId}
                  checked={isSelected}
                  disabled={locked}
                  onCheckedChange={() => toggle(item.id)}
                  aria-label={item.label}
                  aria-describedby={describedBy || undefined}
                  // The primitive greys a disabled box with opacity; the cap uses the DS disabled colours instead.
                  className="disabled:border-ds-disabled-border disabled:bg-ds-disabled-bg disabled:opacity-100"
                />
              </label>
            </li>
          );
        })}
      </ul>
      {error ? (
        <p role="alert" className="text-label text-destructive">
          {error}
        </p>
      ) : null}
    </DockedQuestionCard>
  );
}
